import React, { useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, IconButton, Stack, Tooltip, Typography, alpha, useTheme } from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { SiteHealthService, SiteLiveResponse, SiteLiveStatus } from '../../../services/SiteHealthService';

/**
 * 현장 목록의 서버 상태 칸 — 통합서버 정상 서비스 수/전체, 영상처리서버·NVR 정상 대수/전체.
 * 방재실이 여러 곳인 현장은 방재실마다 한 줄씩(방재실 이름 + 표시) 그린다.
 *
 * 백엔드가 10분마다 각 현장 통합서버(영상처리서버는 통합서버 경유)에 붙어 기대
 * 컨테이너가 돌고 있는지 본 결과(GET /v0.1/site-health/live)를 그린다. 모든 행이
 * 같은 쿼리 키를 써서 요청은 한 번만 나간다. 마우스를 올리면 확인 시각과 사유.
 */
interface Props {
  siteId: string | number;
}

export const LIVE_QUERY_KEY = ['site-health', 'live'];

export function useSiteLiveStatus() {
  return useQuery<SiteLiveResponse>({
    queryKey: LIVE_QUERY_KEY,
    queryFn: ({ signal }) => SiteHealthService.live(signal),
    staleTime: 60_000,
    refetchInterval: 60_000,
    retry: false,
  });
}

// 다시 점검: 요청 후 이 간격으로 결과를 읽어, 마지막 점검 시각이 바뀌면 멈춘다. 현장 하나가
// 응답이 없으면 한 바퀴가 길어지므로 상한을 넘으면 그만 기다린다(다음 정기 점검이 채운다).
const REFRESH_POLL_MS = 3_000;
const REFRESH_GIVE_UP_MS = 120_000;

/**
 * 서버 상태 다시 점검 — 10분 주기를 기다리지 않고 전 현장을 바로 점검하게 하고, 끝나면
 * 목록의 모든 서버 상태 칸이 함께 바뀐다(같은 쿼리 키).
 */
export const ServerStatusRefreshButton: React.FC = () => {
  const queryClient = useQueryClient();
  const [state, setState] = useState<'idle' | 'running' | 'failed'>('idle');
  const mounted = useRef(true);
  // StrictMode 는 effect 를 정리했다 다시 붙인다 — 다시 붙을 때 true 로 돌려놓지 않으면 기다림이 바로 끝난다.
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const refresh = async () => {
    setState('running');
    // 서버 시각과 브라우저 시각이 어긋날 수 있어, 요청 전 last_run_at 이 바뀌는지로 끝을 안다.
    const before = queryClient.getQueryData<SiteLiveResponse>(LIVE_QUERY_KEY)?.last_run_at ?? null;
    try {
      await SiteHealthService.refreshLive();
      const deadline = Date.now() + REFRESH_GIVE_UP_MS;
      while (mounted.current && Date.now() < deadline) {
        await new Promise((resolve) => setTimeout(resolve, REFRESH_POLL_MS));
        const d = await queryClient.fetchQuery<SiteLiveResponse>({
          queryKey: LIVE_QUERY_KEY,
          queryFn: ({ signal }) => SiteHealthService.live(signal),
          staleTime: 0,
        });
        if (!d.enabled || (d.last_run_at ?? null) !== before) break;
      }
      if (mounted.current) setState('idle');
    } catch {
      if (mounted.current) setState('failed');
    }
  };

  const title =
    state === 'running' ? '전 현장 서버 상태를 다시 점검하는 중…' : state === 'failed' ? '다시 점검하지 못했습니다 — 다시 눌러 주세요' : '서버 상태 다시 점검';
  return (
    <Tooltip title={title} arrow>
      <span>
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            void refresh();
          }}
          disabled={state === 'running'}
          color={state === 'failed' ? 'error' : 'default'}
          aria-label="서버 상태 다시 점검"
          data-testid="server-status-refresh"
          // 손가락으로도 누르게 44px, 표 머리줄 높이는 그대로 두게 음수 여백.
          sx={{ p: '12px', my: -1.5 }}
        >
          {state === 'running' ? <CircularProgress size={18} color="inherit" /> : <RefreshIcon fontSize="small" />}
        </IconButton>
      </span>
    </Tooltip>
  );
};

function timeLabel(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getMonth() + 1}/${d.getDate()} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

const Pill: React.FC<{ ok: boolean; label: string; testId?: string }> = ({ ok, label, testId }) => {
  const theme = useTheme();
  const color = ok ? theme.palette.success.main : theme.palette.error.main;
  return (
    <Box
      component="span"
      data-testid={testId}
      data-ok={ok ? 'true' : 'false'}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.6,
        px: 1,
        py: 0.35,
        borderRadius: 1.5,
        fontSize: 12.5,
        fontWeight: 600,
        whiteSpace: 'nowrap',
        fontVariantNumeric: 'tabular-nums',
        color,
        bgcolor: alpha(color, 0.12),
      }}
    >
      <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
      {label}
    </Box>
  );
};

/** 통합서버 칸 — 서비스 수를 알면 영상처리서버 칸처럼 "N개 정상" / "비정상 x/N". */
function serverLabel(s: SiteLiveStatus): string {
  const total = s.services ?? 0;
  if (total === 0) return s.server_ok ? '통합 정상' : '통합 비정상';
  const ok = s.services_ok ?? 0;
  return ok === total ? `통합 ${total}개 정상` : `통합 비정상 ${total - ok}/${total}`;
}

function tooltipFor(s: SiteLiveStatus) {
  return (
    <Box sx={{ py: 0.5 }}>
      <Typography variant="caption" sx={{ display: 'block', opacity: 0.8 }}>
        {s.group ? `${s.group} · ` : ''}
        {timeLabel(s.checked_at)} 확인
      </Typography>
      <Typography variant="caption" sx={{ display: 'block' }}>
        통합서버: {s.server_ok ? '정상' : s.server_reason || '비정상'}
      </Typography>
      {[...s.nodes, ...(s.nvrs || [])].map((n, i) => (
        <Typography key={`${n.name}-${i}`} variant="caption" sx={{ display: 'block' }}>
          {n.name}: {n.ok ? '정상' : n.reason || '비정상'}
        </Typography>
      ))}
    </Box>
  );
}

const Muted: React.FC<{ children: React.ReactNode; title?: string }> = ({ children, title }) => (
  <Typography variant="caption" color="text.secondary" title={title} sx={{ whiteSpace: 'nowrap' }}>
    {children}
  </Typography>
);

const ServerStatusBadge: React.FC<Props> = ({ siteId }) => {
  const { data, isLoading, isError } = useSiteLiveStatus();
  const id = Number(siteId);

  if (isLoading) return <Muted>확인 중…</Muted>;
  if (isError || !data) return <Muted>—</Muted>;
  if (!data.enabled) return <Muted title="이 서버에서는 현장 점검이 꺼져 있습니다">—</Muted>;

  const entries = data.data.filter((x) => x.project_id === id);
  if (entries.length === 0) {
    return data.last_run_at ? (
      <Muted title="현장 설정에 통합서버 접속 정보가 없어 점검하지 않습니다">점검 대상 아님</Muted>
    ) : (
      <Muted title="서버가 막 시작돼 첫 점검을 하는 중입니다">첫 점검 중…</Muted>
    );
  }
  const multi = entries.length > 1;

  return (
    <Stack spacing={0.75} data-testid={`server-status-${id}`} onClick={(e) => e.stopPropagation()}>
      {entries.map((s, i) => {
        const total = s.nodes.length;
        const nvrs = s.nvrs || [];
        const nvrOk = s.nvrs_ok ?? 0;
        const suffix = multi ? `-${i}` : '';
        return (
          <Tooltip key={s.code} title={tooltipFor(s)} arrow placement="bottom-start">
            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
              {multi && s.group && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontWeight: 600, whiteSpace: 'nowrap', minWidth: 0 }}
                  data-testid={`server-status-group-${id}${suffix}`}
                >
                  {s.group}
                </Typography>
              )}
              <Pill
                ok={s.server_ok}
                label={serverLabel(s)}
                testId={`server-status-main-${id}${suffix}`}
              />
              {total > 0 && (
                <Pill
                  ok={s.nodes_ok === total}
                  label={s.nodes_ok === total ? `영상 ${total}대 정상` : `영상 비정상 ${total - s.nodes_ok}/${total}`}
                  testId={`server-status-nodes-${id}${suffix}`}
                />
              )}
              {nvrs.length > 0 && (
                <Pill
                  ok={nvrOk === nvrs.length}
                  label={nvrOk === nvrs.length ? `NVR ${nvrs.length}대 정상` : `NVR 비정상 ${nvrs.length - nvrOk}/${nvrs.length}`}
                  testId={`server-status-nvrs-${id}${suffix}`}
                />
              )}
            </Stack>
          </Tooltip>
        );
      })}
    </Stack>
  );
};

export default ServerStatusBadge;
